import React, { useState } from 'react';

import './ReflectionModal.css';
import { TaskReflectionAgentService } from '../../services/llmClient';
import {
  saveTaskReflection,
  updateTaskTimeSpent,
} from '../../services/supabaseClient/activitiesService';

const ReflectionModal = ({ activity, onClose, onSubmit }) => {
  const [reflection, setReflection] = useState('');
  const [useAI, setUseAI] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiReflection, setAiReflection] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerateAI = async () => {
    setIsGenerating(true);
    setError(null);

    // eslint-disable-next-line no-console
    console.log('[ReflectionModal] Starting AI reflection generation...');

    try {
      const taskData = {
        userId: activity.user_id,
        taskId: activity.id,
        what: activity.what,
        title: activity.what,
        description: activity.task_description || activity.description || '',
        priority: activity.priority || 'medium',
        urgencyLevel: activity.urgencyLevel,
        learningGoal: activity.learningGoal,
        estimatedMinutes: activity.estimated_minutes,
        timeSpent: activity.time_spent || 0,
        scheduledTime: activity.when,
        startedAt: activity.startedAt,
        completedAt: new Date().toISOString(),
        completed: true,
        taskOutcome: 'completed',
        completionPercentage: 100,
        // Include user's reflection for AI to analyze (match service expectation)
        userReflectionResponses: {
          userReflection: reflection,
        },
      };

      // eslint-disable-next-line no-console
      console.log('[ReflectionModal] Calling AI service with taskData:', taskData);

      const generatedReflection =
        await TaskReflectionAgentService.generateQuickReflection(taskData);

      // eslint-disable-next-line no-console
      console.log('[ReflectionModal] AI reflection generated:', generatedReflection);

      setAiReflection(generatedReflection);

      // Save AI reflection AND user's original reflection to database
      // eslint-disable-next-line no-console
      console.log('[ReflectionModal] Saving reflection to database...');

      await saveTaskReflection(activity.id, {
        ...generatedReflection,
        userReflectionResponses: { userReflection: reflection },
      });

      // Update time spent if available
      if (activity.time_spent > 0) {
        await updateTaskTimeSpent(activity.id, activity.time_spent);
      }

      // eslint-disable-next-line no-console
      console.log('[ReflectionModal] Reflection saved successfully!');

      // Reset loading state before submitting
      setIsGenerating(false);

      // Submit with AI reflection
      onSubmit(generatedReflection.aiReflection || '');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ReflectionModal] Failed to generate AI reflection:', err);
      // eslint-disable-next-line no-console
      console.error('[ReflectionModal] Error details:', {
        message: err.message,
        stack: err.stack,
      });
      setError(`Failed to generate AI reflection: ${err.message}`);
      setIsGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (useAI && !aiReflection) {
      // Generate AI reflection with user's answers
      await handleGenerateAI();
    } else {
      // Save manual reflection
      try {
        await saveTaskReflection(activity.id, {
          aiReflection: reflection,
          userReflectionResponses: { userReflection: reflection },
          insights: [],
          patterns: [],
          recommendations: [],
          keyLearnings: [],
        });
        onSubmit(reflection);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save reflection:', err);
        setError('Failed to save reflection. Please try again.');
      }
    }
  };

  const handleSkip = () => {
    onSubmit(null);
  };

  return (
    <div
      className="reflection-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="button"
      tabIndex={0}
      aria-label="Close reflection modal"
    >
      <div className="reflection-modal" role="dialog" aria-modal="true">
        <div className="reflection-modal-header">
          <h2>
            <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
            Task Reflection
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="reflection-modal-close-btn"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <div className="reflection-modal-content">
          <div className="completed-task-info">
            <div className="completed-task-label">Completed Task:</div>
            <div className="completed-task-name">{activity.what}</div>
          </div>

          <form onSubmit={handleSubmit} className="reflection-form">
            {error && (
              <div className="reflection-error">
                <i className="fa-solid fa-exclamation-triangle" aria-hidden="true" />
                {error}
              </div>
            )}

            {/* Single Reflection Question */}
            <div className="reflection-question">
              <label htmlFor="reflection" className="reflection-label">
                <i className="fa-solid fa-lightbulb" aria-hidden="true" />
                How did it go?
              </label>
              <textarea
                id="reflection"
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                placeholder="Share your thoughts about completing this task. What did you learn? What went well? What was challenging?"
                className="reflection-textarea"
                rows="6"
              />
            </div>

            {/* AI Toggle */}
            <div className="ai-toggle-section">
              <label className="ai-toggle-label">
                <input
                  type="checkbox"
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  className="ai-toggle-checkbox"
                />
                <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
                <span>Generate AI reflection from my answers</span>
              </label>
              <p className="ai-toggle-hint">
                AI will analyze your answers and task data to create a comprehensive reflection
              </p>
            </div>

            {isGenerating && (
              <div className="generating-indicator">
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Generating AI reflection...</span>
              </div>
            )}

            <div className="reflection-modal-actions">
              <button
                type="button"
                onClick={handleSkip}
                className="btn-skip"
                disabled={isGenerating}
              >
                Skip for now
              </button>
              <button
                type="submit"
                className="btn-submit-reflection"
                disabled={isGenerating || !reflection.trim()}
              >
                {isGenerating ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check" aria-hidden="true" />
                    {useAI ? 'Generate & Complete' : 'Save Reflection'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReflectionModal;
